// `/transactions/inbox`, design `#s06` -- the uncategorized + transfer-review
// work list. Execution plan §8 WS-5 steps 1-3; category-write shape updated
// per the M2 execution plan §S7.
//
// The heading count and the `inbox-zero` / `api-unavailable` split come
// ONLY from `meta.uncategorized_count` (never guessed, never derived from
// `items.length` when the count itself is missing) -- §6.2 rule 1 /
// binding rule "a count the user can drive to zero."
//
// KNOWN GAP (recorded in docs/memory/category_ai/progress-s7.md, execution
// plan §9 "Known risks": "GET /api/transactions/inbox still does not exist
// ... M2 ... does not fix that older mismatch"): `useInboxItems.ts` is
// frozen/reused-unchanged (§7.4) and its `InboxUncategorizedItem` carries
// neither a `line_item_id` nor a `version` -- both required by the real
// `PATCH /api/line-items/{id}` contract (§8.2). Accepting a suggestion here
// therefore cannot honestly issue that write (there is no real id/version to
// send, and this file must not invent one -- CLAUDE.md "no fabricated or
// hardcoded user-facing values"). `useApplyToSimilar` is still fully wired
// to the real M2 contract (see `CategoryChip`/`TypeChooser`'s "Apply to all
// items from {merchant}" checkbox, via `useCategoryCorrection`) for the
// caller that DOES have real per-item data; this screen surfaces the same
// honest "not available" banner it already used for "Other…" instead of a
// fabricated call. `GET /api/transactions/inbox`
// itself never resolves (see `useInboxItems.ts`), so this screen renders
// `api-unavailable` before any of this becomes reachable in production.
import { useMemo, useState, type KeyboardEvent } from 'react';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { HonestInline } from '@/components/HonestInline';
import { showUndoToast } from '@/components/UndoToast';
import { useShellContext } from '@/app/shell/AppShell';
import { InboxUnavailableError, useInboxItems, type InboxUncategorizedItem } from '@/api/hooks/useInboxItems';
import { useBatchUndo, type ApplyBatchInput } from '@/api/hooks/useBatchUndo';
// Confirming a pairing is a link write, not a categorization batch -- it goes
// through the transactions link endpoint that owns that concept.
import { useConfirmLink } from '@/api/hooks/useConfirmLink';
import { InboxUncategorizedRow } from './components/InboxUncategorizedRow';
import { TransferReviewCard } from './components/TransferReviewCard';
import { InboxBulkBar } from './components/InboxBulkBar';
import { InboxKeyboardLegend } from './components/InboxKeyboardLegend';
import { ViewSwitcher } from '@/features/transactions/ViewSwitcher';

const NOT_WIRED_FROM_INBOX_MESSAGE =
  'Correcting a category from this list needs a live endpoint this build does not have yet. Nothing was changed.';

function InboxPageContent() {
  const { band, readScope, scopeAvailability, retryScopeAvailability } = useShellContext();
  const canonicalReady = scopeAvailability === 'ready' && readScope === null;
  const { data, isLoading, isError, error } = useInboxItems(canonicalReady);
  const batchUndo = useBatchUndo();
  const confirmLink = useConfirmLink();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [writeError, setWriteError] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const uncategorized = useMemo(
    () => items.filter((item): item is InboxUncategorizedItem => item.kind === 'uncategorized'),
    [items],
  );
  const transferReviews = useMemo(() => items.filter((item) => item.kind === 'transfer_review'), [items]);

  const uncategorizedCount = data?.meta?.uncategorized_count ?? null;
  const transferReviewCount = data?.meta?.transfer_review_count ?? null;

  if (scopeAvailability !== 'ready') {
    return (
      <div>
        <ViewSwitcher />
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {scopeAvailability === 'pending' ? 'Checking transaction scope availability…' : <>Transaction scope availability could not be checked.{' '}<button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>Retry</button></>}
        </p>
      </div>
    );
  }

  if (!canonicalReady || error instanceof InboxUnavailableError) {
    return (
      <div>
        <ViewSwitcher />
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Inbox review is not available yet.
        </p>
      </div>
    );
  }

  // The primary Inbox contract is unsupported in this backend. A canonical
  // ready query with no data must expose that fact immediately rather than
  // presenting a loading promise that cannot resolve to Inbox content.
  if (isLoading && !data && !isError) {
    return (
      <div>
        <ViewSwitcher />
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Inbox review is not available yet.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <ViewSwitcher />
        <div className="flex min-h-[30vh] items-center justify-center" role="status" aria-live="polite">
          <p className="text-sm text-muted-foreground">Loading inbox…</p>
        </div>
      </div>
    );
  }

  // §6.2: `inbox-zero` fires only on a REAL zero count; an unknown count is
  // `api-unavailable`, never collapsed into the same state (WS-5 step 1).
  if (isError || uncategorizedCount === null || transferReviewCount === null) {
    return <div><ViewSwitcher /><RouteRecoveryScreen kind="api-unavailable" frame="inline" /></div>;
  }

  if (uncategorizedCount === 0 && transferReviewCount === 0) {
    return (
      <div className="mx-auto max-w-2xl py-6">
        <ViewSwitcher />
        <div className="mt-6">
          <HonestInline kind="inbox-zero" />
        </div>
      </div>
    );
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function applyBatch(input: ApplyBatchInput, messageOnSuccess: string) {
    setWriteError(null);
    batchUndo.apply.mutate(input, {
      onSuccess: (result) => {
        setLastBatchId(result.batch_id);
        showUndoToast({
          message: result.message || messageOnSuccess,
          batchId: result.batch_id,
          onUndo: (batchId) => {
            batchUndo.undo.mutate(batchId, {
              onError: () => setWriteError('Undo is not available yet in this build. Nothing was changed.'),
            });
          },
        });
      },
      onError: () => {
        setWriteError('Categorizing is not available yet in this build. Nothing was changed.');
      },
    });
  }

  // See the file-header "KNOWN GAP" note: this screen has no real
  // line_item_id/version to PATCH with, so accepting a suggestion here
  // surfaces the same honest banner as "Other…" rather than a demo dialog
  // with invented option counts.
  function acceptSuggestion(item: InboxUncategorizedItem) {
    if (!item.suggestion) return;
    setWriteError(NOT_WIRED_FROM_INBOX_MESSAGE);
  }

  function confirmTransfer(candidateId: string, kind: 'transfer' | 'cc_payment') {
    setTransferError(null);
    confirmLink.mutate(
      { candidateId, kind },
      { onError: () => setTransferError('The transfer could not be confirmed. Both rows remain visible.') },
    );
  }

  function chooseBusiness(item: InboxUncategorizedItem, choice: 'personal' | 'business') {
    applyBatch(
      {
        scope: 'single',
        business_use: choice === 'personal' ? 'none' : 'primary',
        transaction_ids: [item.transaction_id],
      },
      `Marked ${item.merchant_display} ${choice}`,
    );
  }

  const selectedItems = uncategorized.filter((item) => selectedIds.has(item.transaction_id));
  const sharedCategory =
    selectedItems.length > 1 && selectedItems.every((item) => item.suggestion)
      ? (() => {
          const first = selectedItems[0].suggestion!.path.join(' › ');
          return selectedItems.every((item) => item.suggestion!.path.join(' › ') === first) ? first : null;
        })()
      : null;

  function onKeyDown(event: KeyboardEvent) {
    if (band !== 'desktop' || uncategorized.length === 0) return;
    const key = event.key.toLowerCase();
    if (key === 'j') {
      setFocusedIndex((i) => Math.min(i + 1, uncategorized.length - 1));
    } else if (key === 'k') {
      setFocusedIndex((i) => Math.max(i - 1, 0));
    } else if (key === 'c') {
      const item = uncategorized[focusedIndex];
      if (item) acceptSuggestion(item);
    } else if (key === 'b') {
      const item = uncategorized[focusedIndex];
      if (item) chooseBusiness(item, 'business');
    } else if (key === 'u') {
      if (lastBatchId) {
        batchUndo.undo.mutate(lastBatchId, {
          onError: () => setWriteError('Undo is not available yet in this build. Nothing was changed.'),
        });
      }
    }
  }

  return (
    <div onKeyDown={onKeyDown} tabIndex={-1}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-normal text-muted-foreground" data-testid="inbox-count">· {uncategorizedCount} uncategorized · {transferReviewCount} transfer review</span>
        <ViewSwitcher />
        {band === 'desktop' && <InboxKeyboardLegend />}
      </div>

      {writeError && (
        <div className="mt-3 rounded-md border border-degraded-border bg-degraded-surface px-3 py-2 text-sm text-degraded-foreground" role="alert">
          {writeError}
        </div>
      )}

      {uncategorizedCount > 0 && (
        <section className="mt-4">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Uncategorized · {uncategorizedCount}</h2>
          <div className={band === 'desktop' ? 'mt-2 rounded-lg border border-border' : ''}>
            {uncategorized.map((item, index) => (
              <div key={item.transaction_id}>
                <InboxUncategorizedRow
                  item={item}
                  band={band}
                  accepted={false}
                  selectable={band !== 'mobile'}
                  selected={selectedIds.has(item.transaction_id)}
                  onToggleSelect={() => toggleSelect(item.transaction_id)}
                  onAcceptSuggestion={() => acceptSuggestion(item)}
                  onOther={() => setWriteError('A full category picker ships in a later cycle. Nothing was changed.')}
                  onBusinessChoice={(choice) => chooseBusiness(item, choice)}
                />
                {band === 'desktop' && index === focusedIndex && <span className="sr-only">focused</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {transferReviewCount > 0 && (
        <section className="mt-6">
          <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Transfer review</h2>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="transfer-denial-unavailable">
            Not a transfer is disabled. Denial is not available yet. Both rows still count until M2.
          </p>
          {transferError && (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {transferError}
            </p>
          )}
          <div className="mt-2 flex flex-col gap-2">
            {transferReviews.map((item) =>
              item.kind === 'transfer_review' ? (
                <TransferReviewCard
                  key={item.group_id}
                  item={item}
                  pending={confirmLink.isPending}
                  onConfirm={(groupId) => confirmTransfer(groupId, item.link_kind)}
                />
              ) : null,
            )}
          </div>
        </section>
      )}

      {band !== 'mobile' && sharedCategory !== null && (
        <InboxBulkBar
          count={selectedItems.length}
          sharedCategoryLabel={sharedCategory}
          pending={batchUndo.apply.isPending}
          onApplyShared={() =>
            applyBatch(
              { scope: 'similar_this_month', category_path: sharedCategory.split(' › '), transaction_ids: Array.from(selectedIds) },
              `Applied ${sharedCategory} to ${selectedItems.length} items`,
            )
          }
          onOther={() => setWriteError('A category picker for bulk selections ships in a later cycle.')}
          onPersonal={() =>
            applyBatch(
              { scope: 'single', business_use: 'none', transaction_ids: Array.from(selectedIds) },
              `Marked ${selectedItems.length} items personal`,
            )
          }
        />
      )}
    </div>
  );
}

export function InboxPage() {
  return (
    <section aria-labelledby="inbox-heading" className="mx-auto max-w-5xl px-4 py-6">
      <h1 id="inbox-heading" tabIndex={-1} className="text-2xl font-bold">Inbox</h1>
      <InboxPageContent />
    </section>
  );
}

export default InboxPage;
